/* Animierte SVG-Szenen für ProcessShowcase — nur transform/opacity, GPU-freundlich */

const fx = (on, dy = 10) => ({
  opacity: on ? 1 : 0,
  transform: on ? "translate(0,0)" : `translate(0,${dy}px)`,
  transition: "opacity .55s ease, transform .75s ease",
});

const loop = (anim) => ({ animation: anim });

/* ============ AUTOGLAS: Steinschlagreparatur ============ */
export const AUTOGLAS_STEPS = [
  { t: "Steinschlag passiert", d: "Ein Stein trifft die Scheibe — es entsteht ein kleiner Krater mit feinen Rissen im Verbundglas." },
  { t: "Schaden prüfen", d: "Wir prüfen Größe und Lage: bis ca. 25 mm und außerhalb des Sichtfelds ist die Reparatur meist möglich." },
  { t: "Spezialharz injizieren", d: "Mit dem Vakuum-Injektor wird UV-härtendes Spezialharz in den Schaden gedrückt — die Luft entweicht, der Riss füllt sich." },
  { t: "UV-Aushärtung & Endkontrolle", d: "Unter der UV-Lampe härtet das Harz aus. Die Scheibe ist wieder stabil — in ca. 30 Minuten erledigt." },
];

export const SteinschlagScene = ({ step, accent }) => (
  <svg viewBox="0 0 480 300" className="w-full h-auto max-h-[320px]" role="img" aria-label="Animierte Darstellung der Steinschlagreparatur">
    <defs>
      <linearGradient id="pxGlass" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#dbeafe" />
        <stop offset="100%" stopColor="#bfdbfe" />
      </linearGradient>
    </defs>
    {/* Windschutzscheibe */}
    <path d="M70 70 Q240 40 410 70 L435 230 Q240 260 45 230 Z" fill="url(#pxGlass)" stroke="#64748b" strokeWidth="4" />
    <path d="M90 85 Q110 78 130 82" stroke="#fff" strokeWidth="5" strokeLinecap="round" opacity="0.7" fill="none" />
    <path d="M100 105 Q130 96 160 102" stroke="#fff" strokeWidth="4" strokeLinecap="round" opacity="0.45" fill="none" />

    {/* Steinschlag-Riss */}
    <g style={{ opacity: step <= 1 ? 1 : step === 2 ? 0.35 : 0, transition: "opacity .8s ease" }}>
      <circle cx="240" cy="150" r="6" fill="#475569" />
      {[0, 45, 100, 160, 210, 265, 310].map((a) => (
        <line
          key={a}
          x1="240" y1="150"
          x2={240 + 26 * Math.cos((a * Math.PI) / 180)}
          y2={150 + 26 * Math.sin((a * Math.PI) / 180)}
          stroke="#475569" strokeWidth="2.5" strokeLinecap="round"
        />
      ))}
    </g>

    {/* Stein fliegt ein (nur Schritt 1) */}
    {step === 0 && (
      <g key="stone" className="px-anim" style={loop("pxStone 2.4s ease-out forwards")}>
        <circle cx="240" cy="150" r="10" fill="#64748b" />
        <circle cx="236" cy="146" r="3" fill="#94a3b8" />
      </g>
    )}

    {/* Lupe + Messkreis (Schritt 2) */}
    <g style={fx(step === 1)}>
      <circle cx="240" cy="150" r="42" fill="none" stroke={accent} strokeWidth="3" strokeDasharray="7 7" className="px-anim" style={loop("pxSpin 14s linear infinite")} />
      <circle cx="290" cy="112" r="30" fill="rgba(255,255,255,.55)" stroke={accent} strokeWidth="5" />
      <line x1="312" y1="134" x2="340" y2="162" stroke={accent} strokeWidth="9" strokeLinecap="round" />
      <text x="240" y="215" textAnchor="middle" fontSize="15" fontWeight="700" fill="#334155">bis ca. 25 mm reparabel</text>
    </g>

    {/* Injektor (Schritt 3) */}
    <g style={fx(step === 2, -12)}>
      <rect x="226" y="62" width="28" height="56" rx="6" fill="#334155" />
      <rect x="233" y="40" width="14" height="28" rx="4" fill={accent} className="px-anim" style={loop("pxDrip 1.6s ease-in-out infinite")} />
      <rect x="236" y="118" width="8" height="24" rx="3" fill="#334155" />
      <circle cx="240" cy="150" r="9" fill={accent} opacity="0.75" className="px-anim" style={loop("pxGlowPulse 1.6s ease-in-out infinite")} />
    </g>

    {/* UV-Lampe + Erfolg (Schritt 4) */}
    <g style={fx(step === 3, -12)}>
      <rect x="190" y="52" width="100" height="22" rx="10" fill="#334155" />
      <ellipse cx="240" cy="120" rx="64" ry="42" fill="#a78bfa" className="px-anim" style={loop("pxGlowPulse 2s ease-in-out infinite")} />
      {[[180, 190], [300, 185], [250, 95]].map(([x, y], i) => (
        <g key={i} className="px-anim" style={{ ...loop(`pxTwinkle2 ${1.6 + i * 0.4}s ease-in-out infinite`), transformBox: "fill-box", transformOrigin: "center" }}>
          <path d={`M${x} ${y - 8} L${x + 2.5} ${y - 2.5} L${x + 8} ${y} L${x + 2.5} ${y + 2.5} L${x} ${y + 8} L${x - 2.5} ${y + 2.5} L${x - 8} ${y} L${x - 2.5} ${y - 2.5} Z`} fill="#fbbf24" />
        </g>
      ))}
      <circle cx="400" cy="225" r="24" fill="#16a34a" />
      <path d="M389 225 L397 233 L412 216" stroke="#fff" strokeWidth="5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </g>
  </svg>
);

/* ============ TÖNUNG / FOLIERUNG ============ */
export const TOENUNG_STEPS = [
  { t: "Scheiben reinigen", d: "Jede Scheibe wird gründlich gereinigt — Staub und Fett würden später Blasen unter der Folie verursachen." },
  { t: "Folie zuschneiden", d: "Die Tönungsfolie mit ABG wird exakt auf die Form Ihrer Scheiben zugeschnitten." },
  { t: "Folie anbringen", d: "Die Folie wird von innen blasenfrei auf die hinteren Seitenscheiben und die Heckscheibe aufgebracht." },
  { t: "Anrakeln & trocknen", d: "Mit dem Rakel wird Restwasser herausgearbeitet — die Folie legt sich glatt und dauerhaft an die Scheibe." },
  { t: "Endkontrolle & ABG-Unterlagen", d: "Wir prüfen jede Scheibe und übergeben Ihnen die Unterlagen für Polizei und TÜV." },
];

export const ToenungScene = ({ step, accent }) => {
  const tintOpacity = step >= 4 ? 0.85 : step === 3 ? 0.7 : step === 2 ? 0.5 : 0;
  return (
    <svg viewBox="0 0 480 300" className="w-full h-auto max-h-[320px]" role="img" aria-label="Animierte Darstellung der Scheibentönung">
      {/* Karosserie */}
      <path d="M50 205 L60 170 Q75 130 130 122 L175 85 Q185 72 205 72 L320 72 Q340 72 352 88 L385 122 Q430 128 445 165 L450 205 Q450 218 435 218 L65 218 Q50 218 50 205 Z" fill="#334155" />
      {/* Fenster vorn (bleibt klar) */}
      <path d="M185 88 L255 88 L255 120 L160 120 Q170 98 185 88 Z" fill="#dbeafe" stroke="#1e293b" strokeWidth="3" />
      {/* Fenster hinten + Heck (werden getönt) */}
      <path d="M265 88 L318 88 Q332 88 340 100 L355 120 L265 120 Z" fill="#dbeafe" stroke="#1e293b" strokeWidth="3" />
      <path d="M265 88 L318 88 Q332 88 340 100 L355 120 L265 120 Z" fill="#0f172a" style={{ opacity: tintOpacity, transition: "opacity 1s ease" }} />
      {/* Räder */}
      <circle cx="140" cy="218" r="30" fill="#0f172a" /><circle cx="140" cy="218" r="13" fill="#94a3b8" />
      <circle cx="360" cy="218" r="30" fill="#0f172a" /><circle cx="360" cy="218" r="13" fill="#94a3b8" />

      {/* Schritt 1: Sprühflasche + Glanz */}
      <g style={fx(step === 0, -10)}>
        <rect x="395" y="60" width="22" height="34" rx="5" fill={accent} />
        <rect x="400" y="48" width="12" height="14" rx="3" fill="#475569" />
        {[[372, 62], [380, 80], [368, 92]].map(([x, y], i) => (
          <circle key={i} cx={x} cy={y} r="3.5" fill="#60a5fa" className="px-anim" style={loop(`pxTwinkle2 ${1.2 + i * 0.3}s ease-in-out infinite`)} />
        ))}
        <path d="M200 98 L214 94" stroke="#fff" strokeWidth="4" strokeLinecap="round" className="px-anim" style={loop("pxGlowPulse 1.8s ease-in-out infinite")} />
        <path d="M285 100 L300 96" stroke="#fff" strokeWidth="4" strokeLinecap="round" className="px-anim" style={loop("pxGlowPulse 2.2s ease-in-out infinite")} />
      </g>

      {/* Schritt 2: Folienbahn + Cutter */}
      <g style={fx(step === 1, -10)}>
        <rect x="150" y="26" width="180" height="34" rx="6" fill="#0f172a" opacity="0.9" />
        <line x1="165" y1="43" x2="315" y2="43" stroke="#94a3b8" strokeWidth="2" strokeDasharray="6 6" />
        <g className="px-anim" style={loop("pxSweep 2.6s ease-in-out infinite")}>
          <path d="M165 34 L180 43 L165 52 Z" fill="#facc15" />
        </g>
      </g>

      {/* Schritt 3: Folie gleitet aufs Fenster */}
      <g style={{ opacity: step === 2 ? 1 : 0, transform: step === 2 ? "translate(0,0)" : "translate(0,-18px)", transition: "opacity .6s ease, transform 1s ease" }}>
        <path d="M268 62 L315 62 L322 74 L268 74 Z" fill="#0f172a" opacity="0.85" />
        <path d="M296 80 L296 86 M292 84 L296 89 L300 84" stroke={accent} strokeWidth="3" fill="none" strokeLinecap="round" />
      </g>

      {/* Schritt 4: Rakel + Bläschen */}
      <g style={fx(step === 3, -8)}>
        <g className="px-anim" style={loop("pxSweep 2.2s ease-in-out infinite")}>
          <rect x="268" y="96" width="26" height="9" rx="3" fill="#facc15" />
          <rect x="277" y="84" width="8" height="16" rx="3" fill="#475569" />
        </g>
        {[[280, 112], [300, 106], [320, 112]].map(([x, y], i) => (
          <circle key={i} cx={x} cy={y} r="3" fill="#93c5fd" className="px-anim" style={loop(`pxTwinkle2 ${1 + i * 0.35}s ease-in-out infinite`)} />
        ))}
      </g>

      {/* Schritt 5: ABG-Badge + Check */}
      <g style={fx(step === 4, 10)}>
        <rect x="60" y="48" width="118" height="34" rx="17" fill="#fff" stroke={accent} strokeWidth="2.5" />
        <text x="119" y="71" textAnchor="middle" fontSize="16" fontWeight="800" fill={accent}>ABG ✓</text>
        <circle cx="420" cy="70" r="24" fill="#16a34a" />
        <path d="M409 70 L417 78 L432 61" stroke="#fff" strokeWidth="5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      </g>
    </svg>
  );
};

/* ============ SATTLEREI: Dachhimmel ============ */
export const SATTLEREI_STEPS = [
  { t: "Der Himmel hängt durch", d: "Der Schaumstoff unter dem Himmelstoff ist zerfallen — der Bezug verliert seinen Halt und hängt herunter." },
  { t: "Dachhimmel ausbauen", d: "Der komplette Himmel wird fachgerecht ausgebaut — inklusive Haltegriffe, Sonnenblenden und Innenleuchten." },
  { t: "Alten Stoff & Schaumreste entfernen", d: "Alter Stoff und zerfallener Schaumstoff werden sorgfältig von der Trägerplatte gelöst und die Fläche vorbereitet." },
  { t: "Neuen Himmelstoff verkleben", d: "Hochwertiger Himmelstoff wird mit passendem Klebstoff gleichmäßig und blasenfrei aufgezogen." },
  { t: "Einbau & Endkontrolle", d: "Nach Ablüft- und Aushärtungszeit wird der Himmel wieder eingebaut — straff wie am ersten Tag." },
];

export const SattlereiScene = ({ step, accent }) => {
  const onBench = step >= 1 && step <= 3;
  return (
    <svg viewBox="0 0 480 300" className="w-full h-auto max-h-[320px]" role="img" aria-label="Animierte Darstellung der Dachhimmel-Erneuerung">
      {/* Fahrzeug-Querschnitt */}
      <path d="M70 120 Q90 62 180 56 L300 56 Q390 62 410 120 L410 150 L70 150 Z" fill="none" stroke="#334155" strokeWidth="5" />
      <line x1="55" y1="150" x2="425" y2="150" stroke="#334155" strokeWidth="5" strokeLinecap="round" />
      {/* Sitz-Andeutung */}
      <path d="M150 150 L150 128 Q150 120 158 120 L172 120" fill="none" stroke="#94a3b8" strokeWidth="4" strokeLinecap="round" opacity="0.6" />
      <path d="M310 150 L310 128 Q310 120 318 120 L332 120" fill="none" stroke="#94a3b8" strokeWidth="4" strokeLinecap="round" opacity="0.6" />

      {/* Werkbank (Schritte 2–4) */}
      <g style={{ opacity: onBench ? 1 : 0, transition: "opacity .6s ease" }}>
        <line x1="120" y1="258" x2="360" y2="258" stroke="#94a3b8" strokeWidth="6" strokeLinecap="round" />
        <line x1="145" y1="258" x2="145" y2="282" stroke="#94a3b8" strokeWidth="5" />
        <line x1="335" y1="258" x2="335" y2="282" stroke="#94a3b8" strokeWidth="5" />
      </g>

      {/* Himmelpanel — wandert zwischen Dach und Werkbank */}
      <g style={{ transform: onBench ? "translate(0,160px)" : "translate(0,0)", transition: "transform 1s cubic-bezier(.4,0,.2,1)" }}>
        {/* Trägerplatte */}
        <rect x="140" y="78" width="200" height="14" rx="7" fill="#64748b" />
        {/* Alter Stoff: hängt durch (Schritt 1), auf Werkbank (2), rutscht ab (3) */}
        <g style={{ opacity: step <= 2 ? 1 : 0, transform: step === 2 ? "translate(70px,18px) rotate(8deg)" : "translate(0,0)", transition: "opacity .7s ease, transform 1s ease", transformBox: "fill-box", transformOrigin: "center" }}>
          <path d={step === 0 ? "M145 92 Q200 96 240 128 Q290 98 335 92 L335 96 Q290 104 240 136 Q195 102 145 96 Z" : "M145 92 L335 92 L335 100 Q240 110 145 100 Z"} fill="#b8a98f" style={{ transition: "d .8s ease" }} />
        </g>
        {/* Schaumstoff-Staub (Schritt 3) */}
        <g style={{ opacity: step === 2 ? 1 : 0, transition: "opacity .5s ease" }}>
          {[[180, 112], [230, 118], [285, 110], [320, 116]].map(([x, y], i) => (
            <circle key={i} cx={x} cy={y} r="3.5" fill="#d6c7a1" className="px-anim" style={loop(`pxDrip ${1.4 + i * 0.3}s ease-in-out infinite`)} />
          ))}
        </g>
        {/* Neuer Stoff (ab Schritt 4) */}
        <g style={{ opacity: step >= 3 ? 1 : 0, transition: "opacity .7s ease" }}>
          <rect x="143" y="90" width="194" height="10" rx="5" fill={accent} style={{ transform: step >= 3 ? "scaleX(1)" : "scaleX(0)", transformOrigin: "143px 95px", transition: "transform 1.1s ease" }} />
        </g>
        {/* Anroll-Werkzeug (Schritt 4) */}
        <g style={{ opacity: step === 3 ? 1 : 0, transition: "opacity .5s ease" }}>
          <g className="px-anim" style={loop("pxSweep 2.4s ease-in-out infinite")}>
            <circle cx="170" cy="96" r="10" fill="#334155" />
            <line x1="178" y1="88" x2="196" y2="66" stroke="#334155" strokeWidth="5" strokeLinecap="round" />
          </g>
        </g>
      </g>

      {/* Erfolg (Schritt 5) */}
      <g style={fx(step === 4, 10)}>
        {[[120, 48], [360, 44]].map(([x, y], i) => (
          <g key={i} className="px-anim" style={{ ...loop(`pxTwinkle2 ${1.6 + i * 0.4}s ease-in-out infinite`), transformBox: "fill-box", transformOrigin: "center" }}>
            <path d={`M${x} ${y - 7} L${x + 2} ${y - 2} L${x + 7} ${y} L${x + 2} ${y + 2} L${x} ${y + 7} L${x - 2} ${y + 2} L${x - 7} ${y} L${x - 2} ${y - 2} Z`} fill="#fbbf24" />
          </g>
        ))}
        <circle cx="420" cy="225" r="24" fill="#16a34a" />
        <path d="M409 225 L417 233 L432 216" stroke="#fff" strokeWidth="5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        <text x="240" y="195" textAnchor="middle" fontSize="15" fontWeight="700" fill="#334155">Straff wie am ersten Tag</text>
      </g>
    </svg>
  );
};
